import { h } from '@/lib/dom';
import { SkeletonList } from '@/components/Skeleton';
import { Table } from '@/components/Table';
import { MarkerBadge } from '@/components/MarkerBadge';
import { PdfLink } from '@/components/PdfLink';
import { data } from '@/lib/data';
import { labPdfUrl } from '@/api/labs';
import { formatDate, formatDelta, formatLabValue, plural } from '@/lib/format';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { pageLayout } from './layout';
import { PANEL_LABELS } from './labs-overview';
import type { MarkerFlag, SubmissionDetail, SubmissionResult } from '@/api/types';

function isOutOfRange(flag: MarkerFlag): boolean {
  return flag === 'H' || flag === 'L' || flag === 'HH' || flag === 'LL';
}

function refText(r: SubmissionResult): string | null {
  if (r.ref_low != null && r.ref_high != null) {
    return `${formatLabValue(r.ref_low)}–${formatLabValue(r.ref_high)}`;
  }
  if (r.ref_high != null) return `<${formatLabValue(r.ref_high)}`;
  if (r.ref_low != null) return `>${formatLabValue(r.ref_low)}`;
  return null;
}

function valueText(r: SubmissionResult): string {
  if (r.value != null) {
    const unit = r.unit && r.unit !== 'unknown' ? ` ${r.unit}` : '';
    return `${formatLabValue(r.value)}${unit}`;
  }
  return r.qualitative_value ?? '—';
}

/** Top strip: one row per out-of-range marker, reusing the home OOR row styling. */
function oorRow(r: SubmissionResult): HTMLElement {
  const isHigh = r.flag === 'H' || r.flag === 'HH';
  const dotColor = isHigh ? 'var(--status-high-fg)' : 'var(--status-low-fg)';
  const ref = refText(r);
  return h('a', {
    href: `/labs/${r.canonical_key}`,
    class: 'out-of-range-row',
    aria: { label: `${r.display_ru}: ${r.value ?? '—'} ${r.unit}, ${isHigh ? 'выше нормы' : 'ниже нормы'}` },
  }, [
    h('span', { class: 'oor-dot', style: `background:${dotColor};` }),
    h('span', { class: 'oor-name' }, r.display_ru),
    h('span', { class: 'oor-value' }, [
      h('span', { class: 'oor-num' }, r.value != null ? formatLabValue(r.value) : '—'),
      ' ',
      h('span', { class: 'oor-flag', style: `color:${dotColor};` }, r.flag ?? ''),
    ]),
    h('span', { class: 'oor-unit text-secondary' }, r.unit && r.unit !== 'unknown' ? r.unit : ''),
    ref ? h('span', { class: 'oor-ref text-tertiary' }, `Норма: ${ref}`) : h('span'),
    h('span', { class: 'oor-arrow text-tertiary' }, '→'),
  ]);
}

function resultsToRows(results: SubmissionResult[]): Array<Record<string, unknown>> {
  return results.map((r) => ({
    canonical_key: r.canonical_key,
    name: r.display_ru,
    value: valueText(r),
    delta: r.delta,
    ref: refText(r) ?? '—',
    flag: r.flag,
    lab_id: r.lab_id,
    has_pdf: r.has_pdf,
    is_oor: isOutOfRange(r.flag),
  }));
}

function panelSection(panel: string, results: SubmissionResult[]): HTMLElement {
  const label = PANEL_LABELS[panel] ?? panel;
  const oorCount = results.filter((r) => isOutOfRange(r.flag)).length;

  const countEl = oorCount > 0
    ? h('span', { class: 'labs-panel-count has-oor' },
        `${oorCount} ${plural(oorCount, ['отклонение', 'отклонения', 'отклонений'])}`)
    : h('span', { class: 'labs-panel-count norm' }, 'норма');

  const header = h('div', { class: 'labs-panel-header' }, [
    h('h2', { class: 'labs-panel-title' }, label),
    countEl,
  ]);

  // Out-of-range first, then alphabetical by name.
  const sorted = results.slice().sort((a, b) => {
    const ao = isOutOfRange(a.flag);
    const bo = isOutOfRange(b.flag);
    if (ao !== bo) return ao ? -1 : 1;
    return a.display_ru.localeCompare(b.display_ru, 'ru');
  });

  const table = Table({
    columns: [
      {
        key: 'name', header: 'Маркёр', render: (row) =>
          h('a', { href: `/labs/${row['canonical_key'] as string}`, class: 'submission-marker-link' },
            row['name'] as string),
      },
      {
        key: 'value', header: 'Значение', align: 'right', width: '120px', render: (row) =>
          h('span', { class: 'tabular' }, row['value'] as string),
      },
      {
        key: 'delta', header: 'Δ', align: 'right', width: '140px', render: (row) => {
          const d = row['delta'] as number | null;
          return h('span', { class: 'tabular text-secondary' }, d != null ? `${formatDelta(d)} vs пред.` : '—');
        },
      },
      {
        key: 'ref', header: 'Норма', align: 'right', width: '130px', render: (row) =>
          h('span', { class: 'text-tertiary' }, row['ref'] as string),
      },
      {
        key: 'flag', header: 'Флаг', align: 'center', width: '64px', render: (row) =>
          MarkerBadge({ flag: (row['flag'] as MarkerFlag) ?? null }),
      },
      {
        key: 'pdf', header: 'PDF', align: 'center', width: '140px', render: (row) =>
          row['has_pdf'] === false
            ? h('span', { class: 'text-tertiary' }, '—')
            : PdfLink({ href: labPdfUrl(row['lab_id'] as number), label: 'Открыть' }),
      },
    ],
    rows: resultsToRows(sorted),
    ariaLabel: `Маркёры: ${label}`,
  });

  return h('div', { class: 'labs-panel' }, [
    header,
    h('div', { class: 'data-table-scroll' }, table),
  ]);
}

function renderContent(sub: SubmissionDetail): HTMLElement[] {
  const out: HTMLElement[] = [];

  const markerCount = sub.total_results;
  const oorCount = sub.out_of_range_count;
  const fileCount = sub.files.length;
  const subtitleParts = [
    `${markerCount} ${plural(markerCount, ['маркёр', 'маркёра', 'маркёров'])}`,
    `${oorCount} ${plural(oorCount, ['отклонение', 'отклонения', 'отклонений'])}`,
    `${fileCount} ${plural(fileCount, ['файл', 'файла', 'файлов'])}`,
  ];

  out.push(h('div', { class: 'labs-header-left' }, [
    h('h1', { class: 'labs-title' }, `Сдача от ${formatDate(sub.drawn_at, 'long')}`),
    h('p', { class: 'labs-subtitle' }, subtitleParts.join(' · ')),
  ]));

  // Out-of-range strip.
  const oorResults = sub.results.filter((r) => isOutOfRange(r.flag));
  if (oorResults.length) {
    // Worst (HH/LL/H/L) then by name.
    const rank = (f: MarkerFlag): number => (f === 'HH' || f === 'LL' ? 0 : 1);
    oorResults.sort((a, b) => rank(a.flag) - rank(b.flag) || a.display_ru.localeCompare(b.display_ru, 'ru'));
    out.push(h('section', { class: 'submission-oor-strip' }, [
      h('div', { class: 'submission-oor-strip-heading' },
        `${oorCount} ${plural(oorCount, ['отклонение', 'отклонения', 'отклонений'])}`),
      h('div', { class: 'oor-list', role: 'list', aria: { label: 'Маркёры вне нормы' } },
        oorResults.map((r) => oorRow(r))),
    ]));
  } else {
    out.push(h('div', { class: 'oor-all-normal' }, [
      h('span', { class: 'oor-norm-check', style: 'color: var(--status-norm-fg);' }, '✓'),
      h('span', null, ' Все показатели сдачи в референсе'),
    ]));
  }

  // Files of this submission.
  out.push(h('div', { class: 'submission-files-heading' }, 'Файлы этой сдачи'));
  out.push(h('div', { class: 'submission-pdf-row' },
    sub.files.map((f) =>
      f.has_pdf !== false
        ? PdfLink({ href: labPdfUrl(f.id), label: f.pdf_filename })
        : h('span', { class: 'text-tertiary submission-pdf-missing' }, `📄 ${f.pdf_filename}`))));

  // Panels: those with out-of-range first, then by label.
  const byPanel = new Map<string, SubmissionResult[]>();
  for (const r of sub.results) {
    if (!byPanel.has(r.panel)) byPanel.set(r.panel, []);
    byPanel.get(r.panel)!.push(r);
  }
  const panels = [...byPanel.entries()].sort((a, b) => {
    const ao = a[1].filter((r) => isOutOfRange(r.flag)).length;
    const bo = b[1].filter((r) => isOutOfRange(r.flag)).length;
    if ((ao > 0) !== (bo > 0)) return ao > 0 ? -1 : 1;
    return (PANEL_LABELS[a[0]] ?? a[0]).localeCompare(PANEL_LABELS[b[0]] ?? b[0], 'ru');
  });
  const panelsWrap = h('div', { class: 'labs-panels' },
    panels.map(([panel, results]) => panelSection(panel, results)));
  out.push(panelsWrap);

  return out;
}

export async function renderSubmission(ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }
  const date = ctx.params['date']!;

  const contentEl = h('div', { id: 'submission-content' }, SkeletonList(6));
  const backLink = h('a', { href: '/labs', class: 'labs-back-link btn-ghost' }, '← Все анализы');
  const root = pageLayout({ user, active: 'labs', children: [backLink, contentEl] });

  void (async () => {
    try {
      const sub = await data.getSubmission(date);
      contentEl.innerHTML = '';
      contentEl.append(...renderContent(sub));
      document.dispatchEvent(new Event('route:settled'));
    } catch (err) {
      contentEl.innerHTML = '';
      const isNotFound = err instanceof Error && /404|не найдена/i.test(err.message);
      contentEl.appendChild(h('div', { class: 'empty-state', role: 'alert' }, [
        h('p', null, isNotFound ? 'Сдача за эту дату не найдена.' : 'Не удалось загрузить сдачу.'),
        isNotFound
          ? h('a', { href: '/labs', class: 'btn btn-secondary mt-2' }, '← Все анализы')
          : h('button', {
              class: 'btn btn-secondary mt-2',
              type: 'button',
              on: { click: () => navigate(window.location.pathname, { replace: true }) },
            }, 'Повторить'),
      ]));
      console.error(err);
    }
  })();

  return root;
}
